// ============================================================================
// COMPLIANCE USER - COMPLIANCE DASHBOARD
// ============================================================================
// Purpose: Overview of compliance work and journey progress
// Mental model: "Show me my compliance status and what I need to do next."
// ============================================================================
// This dashboard follows the documented compliance execution flow:
// 1. View assigned compliance pages
// 2. Upload evidence and fill forms
// 3. Submit pages for review
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import {
  FileText,
  CheckSquare,
  Clock,
  AlertTriangle,
  RefreshCw,
  ArrowRight,
  TrendingUp,
  Shield,
  Target
} from 'lucide-react';
import { complianceExecutionApi, CompliancePage } from '@domains/compliance/execution/api/complianceExecution.api';
import { Award, CheckCircle } from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

interface FrameworkSummary {
  frameworkId: number;
  frameworkName: string;
  frameworkCode: string;
  totalPages: number;
  completedPages: number;
  inProgressPages: number;
  pendingPages: number;
  progress: number;
}

const ComplianceDashboard: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const [pages, setPages] = useState<CompliancePage[]>([]);
  const [frameworks, setFrameworks] = useState<FrameworkSummary[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<FrameworkSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    if (!user?.company_id) return;

    setIsLoading(true);
    try {
      // Load assigned pages
      const pagesData = await complianceExecutionApi.getAssignedPages();
      setPages(pagesData);

      // Group pages by framework
      const frameworkMap = new Map<number, FrameworkSummary>();

      pagesData.forEach(page => {
        const frameworkId = page.framework_id;
        const frameworkName = page.framework_name || `Framework ${frameworkId}`;
        const frameworkCode = `FW-${frameworkId}`;

        if (!frameworkMap.has(frameworkId)) {
          frameworkMap.set(frameworkId, {
            frameworkId,
            frameworkName,
            frameworkCode,
            totalPages: 0,
            completedPages: 0,
            inProgressPages: 0,
            pendingPages: 0,
            progress: 0
          });
        }

        const summary = frameworkMap.get(frameworkId)!;
        summary.totalPages++;

        if (page.status === 'submitted' || page.status === 'approved_for_audit') {
          summary.completedPages++;
        } else if (page.status === 'in_progress') {
          summary.inProgressPages++;
        } else {
          summary.pendingPages++;
        }
      });

      // Calculate progress for each framework
      const frameworkSummaries = Array.from(frameworkMap.values()).map(fw => ({
        ...fw,
        progress: fw.totalPages > 0 ? Math.round((fw.completedPages / fw.totalPages) * 100) : 0
      }));

      setFrameworks(frameworkSummaries);

      // Select first framework by default
      if (frameworkSummaries.length > 0) {
        setSelectedFramework(frameworkSummaries[0] || null);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStats = () => {
    const total = pages.length;
    const pending = pages.filter(p => p.status === 'pending' || p.status === 'not_started').length;
    const inProgress = pages.filter(p => p.status === 'in_progress').length;
    const submitted = pages.filter(p => p.status === 'submitted' || p.status === 'approved_for_audit').length;

    return { total, pending, inProgress, submitted };
  };

  const getPriorityPages = () => {
    // Get high priority and overdue pages
    const now = new Date();
    return pages
      .filter(page => {
        const isHighPriority = page.priority === 'critical' || page.priority === 'high';
        const isOverdue = page.due_date && new Date(page.due_date) < now;
        const isPending = page.status === 'pending' || page.status === 'not_started';
        return (isHighPriority || isOverdue) && isPending;
      })
      .slice(0, 5); // Show top 5
  };

  const getRecentPages = () => {
    // Get recently updated pages
    return [...pages]
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 5);
  };

  const stats = getStats();
  const priorityPages = getPriorityPages();
  const recentPages = getRecentPages();

  if (!hasPermission('compliance.dashboard.view')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="bg-red-600 px-6 py-5">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-white" size={28} />
              <div>
                <h1 className="text-xl font-bold text-white">Access Denied</h1>
                <p className="text-red-100 mt-0.5 text-sm">Restricted Area</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <p className="text-slate-600 text-sm">You don't have permission to view the compliance dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-600 font-medium text-sm">Loading compliance dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Compliance Dashboard</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm mt-0.5">Track your compliance progress and assigned tasks</p>
          </div>
          <button
            onClick={loadDashboardData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>

        {/* Compliance Progress Summary */}
        {frameworks.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-6" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-4 h-4 text-indigo-600" />
              <h3 className="text-sm font-semibold text-slate-800">Your Compliance Progress</h3>
            </div>
            <div className="space-y-3">
              {frameworks.map(fw => {
                const pct = fw.total > 0 ? Math.round(((fw.total - fw.pending - fw.inProgress) / fw.total) * 100) : 0;
                const allDone = fw.pending === 0 && fw.inProgress === 0 && fw.total > 0;
                return (
                  <div key={fw.frameworkId} className={`p-3 rounded-xl border ${allDone ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800">{fw.frameworkName}</span>
                        {allDone && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">
                            <Award className="w-3 h-3" /> COMPLETE
                          </span>
                        )}
                      </div>
                      <span className={`text-sm font-bold ${allDone ? 'text-emerald-600' : 'text-slate-700'}`}>{pct}%</span>
                    </div>
                    <div className="w-full bg-white rounded-full h-2">
                      <div className={`h-2 rounded-full transition-all duration-500 ${allDone ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex gap-3 mt-2 text-xs text-slate-500">
                      <span>{fw.submitted} submitted</span>
                      <span>{fw.inProgress} in progress</span>
                      <span>{fw.pending} pending</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* My Next Action Banner */}
        {(() => {
          const priorityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
          const now = new Date();
          const pendingPages = pages
            .filter(p => p.status === 'not_started' || p.status === 'in_progress' || p.status === 'pending')
            .sort((a, b) => {
              // Overdue first
              const aOverdue = a.due_date && new Date(a.due_date) < now ? 0 : 1;
              const bOverdue = b.due_date && new Date(b.due_date) < now ? 0 : 1;
              if (aOverdue !== bOverdue) return aOverdue - bOverdue;
              // Then by priority
              const aPri = priorityOrder[a.priority || 'medium'] ?? 2;
              const bPri = priorityOrder[b.priority || 'medium'] ?? 2;
              if (aPri !== bPri) return aPri - bPri;
              // Then by due date
              if (a.due_date && b.due_date) return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
              return 0;
            });
          const firstPage = pendingPages[0];
          if (pendingPages.length === 0) return null;
          return (
            <div className="bg-gradient-to-r from-indigo-50 via-blue-50 to-sky-50 border border-indigo-200 rounded-2xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-indigo-900">
                    You have {pendingPages.length} page{pendingPages.length !== 1 ? 's' : ''} to complete
                  </p>
                  <p className="text-xs text-indigo-600">
                    Next: {firstPage?.title || 'View your assigned pages'}
                  </p>
                </div>
              </div>
              {firstPage && (
                <a href={`/compliance/pages/${firstPage.id}`}
                  className="px-4 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transition-colors">
                  Start Working
                </a>
              )}
            </div>
          );
        })()}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center">
              <div className="p-2.5 bg-slate-100 rounded-lg">
                <FileText className="text-slate-600" size={20} />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-slate-500">Total Pages</p>
                <p className="text-xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center">
              <div className="p-2.5 bg-yellow-100 rounded-lg">
                <AlertTriangle className="text-yellow-600" size={20} />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-slate-500">Pending</p>
                <p className="text-xl font-bold text-slate-900">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center">
              <div className="p-2.5 bg-blue-100 rounded-lg">
                <Clock className="text-blue-600" size={20} />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-slate-500">In Progress</p>
                <p className="text-xl font-bold text-slate-900">{stats.inProgress}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center">
              <div className="p-2.5 bg-green-100 rounded-lg">
                <CheckSquare className="text-green-600" size={20} />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-slate-500">Submitted</p>
                <p className="text-xl font-bold text-slate-900">{stats.submitted}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Framework Progress */}
        {frameworks.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <TrendingUp size={18} className="text-blue-600" />
                Framework Progress
              </h2>
              <Link
                to="/compliance/frameworks"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
              >
                View All
                <ArrowRight size={15} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {frameworks.map(framework => (
                <div
                  key={framework.frameworkId}
                  className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors cursor-pointer"
                  onClick={() => setSelectedFramework(framework)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-slate-900 truncate text-sm">{framework.frameworkName}</h3>
                    <span className="text-sm font-bold text-slate-900">{framework.progress}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 mb-2">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        framework.progress >= 75 ? 'bg-green-500' :
                        framework.progress >= 50 ? 'bg-blue-500' :
                        framework.progress >= 25 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${framework.progress}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{framework.completedPages}/{framework.totalPages} completed</span>
                    <span>{framework.pendingPages} pending</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Priority Tasks */}
        {priorityPages.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Target size={18} className="text-red-600" />
                Priority Tasks
              </h2>
              <Link
                to="/compliance/tasks"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
              >
                View All Tasks
                <ArrowRight size={15} />
              </Link>
            </div>
            <div className="space-y-3">
              {priorityPages.map(page => (
                <Link
                  key={page.id}
                  to={`/compliance/pages/${page.id}`}
                  className="block border border-slate-200 rounded-lg p-4 hover:border-blue-300 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          page.priority === 'critical' ? 'bg-red-100 text-red-700' :
                          page.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {page.priority}
                        </span>
                        {page.due_date && new Date(page.due_date) < new Date() && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                            Overdue
                          </span>
                        )}
                      </div>
                      <h3 className="font-medium text-slate-900 text-sm mb-1">{page.title}</h3>
                      <p className="text-xs text-slate-500">{page.framework_name}</p>
                    </div>
                    <ArrowRight className="text-slate-400" size={18} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {recentPages.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Clock size={18} className="text-blue-600" />
                Recent Activity
              </h2>
              <Link
                to="/compliance/tasks"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
              >
                View All
                <ArrowRight size={15} />
              </Link>
            </div>
            <div className="space-y-3">
              {recentPages.map(page => (
                <Link
                  key={page.id}
                  to={`/compliance/pages/${page.id}`}
                  className="block border border-slate-200 rounded-lg p-4 hover:border-blue-300 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          page.status === 'submitted' || page.status === 'approved_for_audit' ? 'bg-green-100 text-green-700' :
                          page.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {page.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                      <h3 className="font-medium text-slate-900 text-sm mb-1">{page.title}</h3>
                      <p className="text-xs text-slate-500">{page.framework_name}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Updated {new Date(page.updated_at).toLocaleString()}
                      </p>
                    </div>
                    <ArrowRight className="text-slate-400" size={18} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {pages.length === 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-12">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-slate-100 mb-4">
              <FileText className="text-slate-400" size={28} />
            </div>
            <h3 className="text-base font-semibold text-slate-900 mb-2">No compliance pages assigned</h3>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              You don't have any compliance pages assigned to you yet. Contact your company admin to get started.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceDashboard;
